import { z } from 'zod';
import { PERMISSION_KEYS, type Permission } from './access.js';

/**
 * Инструкции: пакет (zip) с guide.json и папкой images/. Готовит его автор инструкций
 * (скриншоты снимаются по настройкам организации), администратор загружает на сайт.
 * Сайт показывает инструкцию пошагово или целиком и собирает из неё PDF.
 */

/** Область скриншота, которую надо подсветить: доли ширины и высоты (0…1). */
export const guideHighlightSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});
export type GuideHighlight = z.infer<typeof guideHighlightSchema>;

export const guideStepSchema = z.object({
  title: z.string().trim().min(1, 'У шага нет заголовка').max(200),
  /** Текст шага; абзацы — через пустую строку, **жирный** — в двойных звёздочках. */
  text: z.string().trim().max(4000).default(''),
  /** Файл из папки images/ пакета (PNG или JPEG); без картинки — шаг только с текстом. */
  image: z.string().regex(/^[\w.-]{1,120}\.(png|jpe?g)$/i, 'Картинка — PNG или JPEG из папки images/').nullable().default(null),
  highlight: guideHighlightSchema.nullable().default(null),
  /** Подсказка под шагом («Если кнопка неактивна…»). */
  tip: z.string().trim().max(1000).nullable().default(null),
});
export type GuideStep = z.infer<typeof guideStepSchema>;

/** Для кого инструкция: всем или тем, у кого есть право (например, routes.manage). */
export const GUIDE_AUDIENCES = ['all', ...PERMISSION_KEYS] as const;
export type GuideAudience = 'all' | Permission;

export const guideManifestSchema = z.object({
  key: z.string().regex(/^[a-z0-9-]{1,64}$/, 'Ключ инструкции — латиница, цифры и дефис'),
  title: z.string().trim().min(1, 'Нет названия инструкции').max(200),
  description: z.string().trim().max(1000).default(''),
  /** Эмодзи на карточке. */
  icon: z.string().max(16).default('📘'),
  audience: z.enum(GUIDE_AUDIENCES as unknown as [string, ...string[]]).default('all'),
  /** Порядок в списке: меньше — выше. */
  order: z.number().int().min(0).max(10_000).default(100),
  steps: z.array(guideStepSchema).min(1, 'В инструкции нет шагов').max(200),
});
export type GuideManifest = z.infer<typeof guideManifestSchema>;
export type GuideManifestInput = z.input<typeof guideManifestSchema>;

export interface GuideSummaryDto {
  key: string;
  title: string;
  description: string;
  icon: string;
  audience: GuideAudience;
  order: number;
  steps: number;
  version: number;
  updatedAt: string;
}

export interface GuideDto extends Omit<GuideSummaryDto, 'steps'> {
  /** image — адрес картинки на сайте (или null). */
  steps: Array<Omit<GuideStep, 'image'> & { image: string | null }>;
}
